import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AxiosResponse } from 'axios';

import axios from '../../../sharedModules/utils/axios.utils';
import type { Media } from '../../../sharedModules/imagePreview/media';
import { ReceivingDetail, ReceivingLineItem, ReceivingMedia, postReceivingPhotos } from '../model';

export type ReceivingPhotoKind = 'package' | 'equipment';

export interface UseReceivingDetailArgs {
  poNumber: string;
  location: string;
  poValidated: boolean;
  reloadToken: number;
  onError: (message: string) => void;
}

export interface UseReceivingDetailResult {
  lineItems: ReceivingLineItem[];
  dirty: boolean;
  saving: boolean;
  saveLineItems: () => Promise<void>;
  addLine: () => void;
  removeLine: (index: number) => void;
  updateLine: (index: number, patch: Partial<ReceivingLineItem>) => void;
  hasScan: boolean;
  documentPhotos: Media[];
  packagePhotos: Media[];
  equipmentPhotos: Media[];
  uploads: ReceivingPhotoKind[];
  uploadPhotos: (files: File[], kind: ReceivingPhotoKind) => Promise<void>;
  removePhoto: (id: number | string) => Promise<void>;
  loading: boolean;
}

const EMPTY_LINE: ReceivingLineItem = { description: '', quantity: '' };

const toGalleryMedia = (item: ReceivingMedia): Media => ({
  id: item.id,
  url: item.url,
  content_type: item.content_type,
});

export const useReceivingDetail = ({
  poNumber,
  location,
  poValidated,
  reloadToken,
  onError,
}: UseReceivingDetailArgs): UseReceivingDetailResult => {
  const [lineItems, setLineItems] = useState<ReceivingLineItem[]>([{ ...EMPTY_LINE }]);
  const [media, setMedia] = useState<ReceivingMedia[]>([]);
  const [hasScan, setHasScan] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // One entry per batch in flight, not a flag: a receiver can start a second batch before the
  // first resolves, and a boolean would let the first response clear busy while the other upload
  // is still going. Entries carry their kind so each section reports only its own progress.
  const [uploads, setUploads] = useState<ReceivingPhotoKind[]>([]);

  // Every write here resolves after an await, by which time the receiver may have moved to another
  // PO or submitted and reset the page. Read at apply time to decide whether the response still
  // belongs to what is on screen; a ref rather than state so applyDetail needs no dependencies.
  const poNumberRef = useRef<string>(poNumber);
  poNumberRef.current = poNumber;

  const documentPhotos = useMemo(() => media.filter((item) => item.kind === 'document').map(toGalleryMedia), [media]);
  const packagePhotos = useMemo(() => media.filter((item) => item.kind === 'package').map(toGalleryMedia), [media]);
  // Matched explicitly rather than as "not a document": the old predicate would have swallowed
  // every package photo into the equipment gallery the moment the new kind appeared.
  const equipmentPhotos = useMemo(() => media.filter((item) => item.kind === 'equipment').map(toGalleryMedia), [media]);

  // Drop the previous PO's media the moment the number changes: until its load resolves it would
  // otherwise show that PO's paperwork under this number, and vouch for a document this PO may
  // not have. Keyed on poNumber alone so a post-scan reloadToken refresh does not blank the gallery.
  useEffect(() => {
    setMedia([]);
  }, [poNumber]);

  // requestedFor is the PO this response was asked for. A response for any other PO is dropped:
  // applying it would show one PO's paperwork and items under another's number, or repopulate the
  // form that a finished submit just cleared.
  const applyDetail = useCallback((detail: ReceivingDetail, requestedFor: string) => {
    if (requestedFor !== poNumberRef.current) return;

    setLineItems(detail.line_items?.length ? detail.line_items : [{ ...EMPTY_LINE }]);
    setMedia(detail.media || []);
    // The review warning is only honest when a scan produced the table. Telling a receiver their
    // own typing might have been misread is worse than saying nothing.
    setHasScan((detail.extracted_line_items || []).length > 0);
    setDirty(false);
  }, []);

  const clearSection = useCallback(() => {
    setLineItems([{ ...EMPTY_LINE }]);
    setMedia([]);
    setHasScan(false);
    setDirty(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!poNumber) {
      // Page flags (document/package/unsaved) are derived from this hook now, so emptying the
      // PO must actually clear — the old section could leave dirty media because the page reset
      // those flags itself.
      clearSection();
      return;
    }

    // Gated on the lookup, not on the PO's shape, so one predicate governs loading and editing
    // alike. Two predicates diverged in both directions: a PO the lookup accepts but the format
    // rejects unlocked every control while never loading the receiving - leaving Submit
    // permanently disabled, since the flags it reads come from the media this fetch returns.
    if (!poValidated) {
      clearSection();
      return;
    }

    const controller = new AbortController();
    let stale = false;

    const load = async () => {
      setLoading(true);

      try {
        const response = await axios.get<ReceivingDetail>('/warehouse/receiving/photo_sets', {
          params: { po_number: poNumber, location },
          signal: controller.signal,
        });
        if (stale) return;
        applyDetail(response.data, poNumber);
      } catch (error: any) {
        if (
          stale ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError'
        ) {
          return;
        }
        onError(error?.response?.data?.error || 'Something went wrong loading this PO.');
      } finally {
        if (!stale) setLoading(false);
      }
    };

    load();

    return () => {
      stale = true;
      controller.abort();
    };
  }, [poNumber, poValidated, location, reloadToken, applyDetail, onError, clearSection]);

  const updateLine = (index: number, patch: Partial<ReceivingLineItem>) => {
    setLineItems((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
    setDirty(true);
  };

  const addLine = () => {
    setLineItems((current) => [...current, { ...EMPTY_LINE }]);
    setDirty(true);
  };

  const removeLine = (index: number) => {
    setLineItems((current) => current.filter((_, i) => i !== index));
    setDirty(true);
  };

  const saveLineItems = async () => {
    const requestedFor = poNumber;
    setSaving(true);

    try {
      const response = await axios.put<unknown, AxiosResponse<ReceivingDetail>>('/warehouse/receiving/line_items', {
        po_number: poNumber,
        location,
        line_items: lineItems.map((line) => ({
          description: line.description,
          quantity: line.quantity === '' ? null : line.quantity,
        })),
      });
      applyDetail(response.data, requestedFor);
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong saving these items.');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async (files: File[], kind: ReceivingPhotoKind) => {
    if (files.length === 0) return;
    if (!poValidated) {
      onError('Look up a PO number before adding photos.');
      return;
    }

    const requestedFor = poNumber;
    setUploads((current) => [...current, kind]);

    try {
      const response = await postReceivingPhotos(kind, files, poNumber, location);
      applyDetail(response.data, requestedFor);
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong uploading those photos.');
    } finally {
      // Drops one entry for this kind, not every entry: a second batch of the same kind may
      // still be in flight.
      setUploads((current) => current.filter((_, index) => index !== current.indexOf(kind)));
    }
  };

  const removePhoto = async (id: number | string) => {
    try {
      await axios.delete(`/warehouse/receiving/photo_set_attachments/${id}`);
      setMedia((current) => current.filter((item) => item.id !== id));
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong deleting that photo.');
    }
  };

  return {
    lineItems,
    dirty,
    saving,
    saveLineItems,
    addLine,
    removeLine,
    updateLine,
    hasScan,
    documentPhotos,
    packagePhotos,
    equipmentPhotos,
    uploads,
    uploadPhotos,
    removePhoto,
    loading,
  };
};
